import { Button, IconButton, Stack, Typography } from '@mui/material';
import { SelectOption, useGetTimetableStructuresQuery } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  ArrowRightIcon,
  CrossIcon,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalMain,
  ModalSmall,
  PublishIcon,
} from '@schooly/style';
import { FC, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form-lts';
import { FormattedMessage, useIntl } from 'react-intl';

import FormSelect2 from '../../../../components/ui/Input/FormSelect2';

type CreateTimetableSetupForm = { school_year_id: string; structure_id: string };

interface CreateTimetableSelectModalProps {
  schoolYears: { id: string; name: string }[];
  isLoadingSchoolYears: boolean;
  onClose: () => void;
  onCreate: (schoolYearId: string, structureId: string) => void;
  onImport: (schoolYearId: string, structureId: string) => void;
}

export const CreateTimetableSelectModal: FC<CreateTimetableSelectModalProps> = ({
  schoolYears,
  isLoadingSchoolYears,
  onClose,
  onCreate,
  onImport,
}) => {
  const { formatMessage } = useIntl();
  const { schoolId = '' } = useAuth();

  const form = useForm<CreateTimetableSetupForm>({
    defaultValues: { school_year_id: '', structure_id: '' },
  });

  const { data: structuresData, isLoading: isLoadingStructures } = useGetTimetableStructuresQuery(
    { school_id: schoolId },
    { enabled: !!schoolId, refetchOnMount: 'always' },
  );

  const schoolYearOptions: SelectOption[] = useMemo(
    () => schoolYears.map((y) => ({ value: y.id, label: y.name })),
    [schoolYears],
  );

  const structureOptions: SelectOption[] = useMemo(
    () => (structuresData?.results ?? []).map((s) => ({ value: s.id, label: s.name })),
    [structuresData],
  );

  const handleImport = form.handleSubmit((data) => {
    onImport(data.school_year_id, data.structure_id);
  });

  const handleCreate = form.handleSubmit((data) => {
    onCreate(data.school_year_id, data.structure_id);
  });

  return (
    <ModalSmall
      open
      onClose={onClose}
      sx={(theme) => ({
        [theme.breakpoints.down('lg')]: {
          alignItems: 'center',
        },
        '& .MuiDialog-paperFullWidth': {
          height: 'unset',
          [theme.breakpoints.down('lg')]: {
            width: 600,
          },
        },
      })}
    >
      <FormProvider {...form}>
        <form>
          <ModalHeader active title={formatMessage({ id: 'school-timetables-CreateTimetable' })}>
            <IconButton onClick={onClose}>
              <CrossIcon />
            </IconButton>
          </ModalHeader>

          <ModalMain>
            <ModalContent active>
              <Stack gap={2.5}>
                <Stack gap={1}>
                  <Typography variant="h2">
                    <FormattedMessage id="school-timetables-Create-WhichSchoolYear" />
                  </Typography>
                  <FormSelect2
                    name="school_year_id"
                    labelTextId="schoolYear"
                    options={schoolYearOptions}
                    isLoading={isLoadingSchoolYears}
                    rules={{ required: true }}
                  />
                </Stack>

                <Stack gap={1}>
                  <Typography variant="h2">
                    <FormattedMessage id="school-timetables-Create-WhichTimetableStructure" />
                  </Typography>
                  <FormSelect2
                    name="structure_id"
                    labelTextId="school-sections-TimetableStructure"
                    options={structureOptions}
                    isLoading={isLoadingStructures}
                    rules={{ required: true }}
                  />
                </Stack>
              </Stack>
            </ModalContent>
          </ModalMain>

          <ModalFooter active>
            <Stack direction="row" gap={1.25}>
              <Button
                variant="outlined"
                startIcon={<PublishIcon className="reset-svg-currentColor" />}
                onClick={handleImport}
              >
                {formatMessage({ id: 'school-timetables-Import' })}
              </Button>
              <Button endIcon={<ArrowRightIcon />} onClick={handleCreate}>
                {formatMessage({ id: 'school-timetables-CreateFromScratch' })}
              </Button>
            </Stack>
          </ModalFooter>
        </form>
      </FormProvider>
    </ModalSmall>
  );
};
